'use client';

import React, { useRef } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/lib/i18n/useTranslation';

gsap.registerPlugin(ScrollTrigger);

const GOLD = '#D48937';

export default function FAQPage() {
    const containerRef = useRef(null);
    const { t, locale } = useTranslation();

    const shipping = t('faq.shipping') || {};
    const columns = shipping.columns || {};
    const rows = Array.isArray(shipping.rows) ? shipping.rows : [];
    const items = t('faq.items');
    const faqItems = Array.isArray(items) ? items : [];

    useGSAP(
        () => {
            const mm = gsap.matchMedia();
            mm.add('(prefers-reduced-motion: no-preference)', () => {
                const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });
                tl.from('[data-faq-eyebrow]', { autoAlpha: 0, y: 12, duration: 1 }, 0)
                  .from('[data-faq-title]', { autoAlpha: 0, x: -32, duration: 1.4 }, 0.1)
                  .from('[data-faq-rule]', { scaleX: 0, transformOrigin: 'left center', duration: 1.2 }, 0.3)
                  .from('[data-faq-body]', { autoAlpha: 0, y: 24, duration: 1.2 }, 0.45);
            });
        },
        { scope: containerRef, dependencies: [locale] }
    );

    return (
        <>
            <div
                ref={containerRef}
                className="relative z-20 min-h-screen bg-gradient-to-br from-white to-gray-200 text-neutral-800"
            >
                <Navbar />

                <main className="px-6 pb-32 pt-28 md:px-12 md:pt-24 lg:px-20">
                    <div key={locale} className="mx-auto max-w-4xl">
                        {/* Header */}
                        <header className="mb-14 md:mb-20">
                            <p
                                data-faq-eyebrow
                                className="mb-3 text-[10px] uppercase tracking-[0.35em]"
                                style={{ color: GOLD }}
                            >
                                {t('faq.eyebrow')}
                            </p>
                            <h1
                                data-faq-title
                                className="font-pp-neue text-[clamp(2.5rem,6vw,5rem)] font-normal leading-none tracking-tight text-neutral-900"
                            >
                                {t('faq.title')}
                            </h1>
                            <div
                                data-faq-rule
                                className="mt-5 h-px w-full"
                                style={{ background: `linear-gradient(to right, ${GOLD}, transparent)` }}
                            />
                            <p className="mt-6 max-w-prose text-sm leading-relaxed text-neutral-600 md:text-base">
                                {t('faq.subtitle')}
                            </p>
                        </header>

                        <div data-faq-body className="space-y-16 md:space-y-24">
                            {/* Shipping */}
                            <section>
                                <h2 className="font-pp-neue text-2xl font-normal tracking-tight text-neutral-900 md:text-3xl">
                                    {shipping.heading}
                                </h2>
                                <p className="mt-4 max-w-prose text-sm leading-relaxed text-neutral-600 md:text-base">
                                    {shipping.intro}
                                </p>

                                <div className="mt-8 overflow-x-auto">
                                    <table className="w-full min-w-[30rem] text-sm md:text-base">
                                        <thead>
                                            <tr className="border-b border-neutral-300 text-left text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                                                <th className="py-3 pr-4 font-normal">{columns.category}</th>
                                                <th className="py-3 px-3 text-right font-normal">{columns.de}</th>
                                                <th className="py-3 px-3 text-right font-normal">{columns.eu}</th>
                                                <th className="py-3 pl-3 text-right font-normal">{columns.world}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-neutral-200">
                                            {rows.map((row, i) => (
                                                <tr key={i}>
                                                    <td className="py-4 pr-4 text-neutral-900">{row.category}</td>
                                                    <td className="py-4 px-3 text-right tabular-nums text-neutral-700">{row.de}</td>
                                                    <td className="py-4 px-3 text-right tabular-nums text-neutral-700">{row.eu}</td>
                                                    <td className="py-4 pl-3 text-right tabular-nums text-neutral-700">{row.world}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {shipping.note && (
                                    <p className="mt-5 max-w-prose text-xs leading-relaxed text-neutral-500 md:text-sm">
                                        {shipping.note}
                                    </p>
                                )}
                            </section>

                            {/* Questions */}
                            <section>
                                <h2 className="mb-2 font-pp-neue text-2xl font-normal tracking-tight text-neutral-900 md:text-3xl">
                                    {t('faq.questionsHeading')}
                                </h2>
                                <div className="border-t border-neutral-200">
                                    {faqItems.map((item, i) => (
                                        <details key={i} className="group border-b border-neutral-200 py-5" open={i === 0}>
                                            <summary className="flex cursor-pointer list-none items-center justify-between gap-6">
                                                <span className="pr-2 text-base text-neutral-900 md:text-lg">
                                                    {item.question}
                                                </span>
                                                <span
                                                    className="shrink-0 text-2xl leading-none transition-transform duration-300 group-open:rotate-45"
                                                    style={{ color: GOLD }}
                                                    aria-hidden="true"
                                                >
                                                    +
                                                </span>
                                            </summary>
                                            <p className="mt-4 max-w-prose text-sm leading-relaxed text-neutral-600 md:text-base">
                                                {item.answer}
                                            </p>
                                        </details>
                                    ))}
                                </div>
                            </section>
                        </div>
                    </div>
                </main>
            </div>

            <Footer />
        </>
    );
}
